/** Compress images for inline rich-text embedding (data URLs). */

export const RICH_IMAGE_MAX_WIDTH = 1400
export const RICH_IMAGE_MAX_DATA_URL_BYTES = Math.floor(1.5 * 1024 * 1024)
export const RICH_IMAGE_JPEG_QUALITY = 0.82

/**
 * Resize and compress an image File/Blob to a data URL suitable for TipTap.
 * Prefers JPEG for photos; keeps PNG when the source has transparency and is small enough.
 */
export async function fileToCompressedDataUrl(file) {
  if (!file || !String(file.type || '').startsWith('image/')) {
    throw new Error('Please choose an image file.')
  }

  const bitmap = await createImageBitmap(file)
  try {
    const scale = Math.min(1, RICH_IMAGE_MAX_WIDTH / Math.max(bitmap.width, 1))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not process image.')
    ctx.drawImage(bitmap, 0, 0, width, height)

    const preferPng =
      file.type === 'image/png' || file.type === 'image/gif' || file.type === 'image/webp'

    let dataUrl = canvas.toDataURL('image/jpeg', RICH_IMAGE_JPEG_QUALITY)
    if (preferPng) {
      const pngUrl = canvas.toDataURL('image/png')
      // Use PNG only when it is not much larger than JPEG
      if (pngUrl.length <= dataUrl.length * 1.15) {
        dataUrl = pngUrl
      }
    }

    if (dataUrl.length > RICH_IMAGE_MAX_DATA_URL_BYTES) {
      // Retry at stronger compression / smaller width
      const retryScale = Math.min(1, 1000 / Math.max(bitmap.width, 1))
      canvas.width = Math.max(1, Math.round(bitmap.width * retryScale))
      canvas.height = Math.max(1, Math.round(bitmap.height * retryScale))
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      dataUrl = canvas.toDataURL('image/jpeg', 0.7)
    }

    if (dataUrl.length > RICH_IMAGE_MAX_DATA_URL_BYTES) {
      throw new Error(
        'Image is too large after compression. Try a smaller screenshot (about 1.5 MB max).',
      )
    }

    return dataUrl
  } finally {
    bitmap.close?.()
  }
}

/**
 * Insert a compressed image at the current TipTap selection.
 * @returns {Promise<string|null>} error message or null on success
 */
export async function insertCompressedImage(editor, file) {
  if (!editor || !file) return 'No image selected.'
  try {
    const src = await fileToCompressedDataUrl(file)
    editor.chain().focus().setImage({ src }).run()
    return null
  } catch (err) {
    return err?.message || 'Could not insert image.'
  }
}

/**
 * TipTap paste handler: if clipboard has an image file, insert it and prevent default.
 */
export function handleRichTextImagePaste(editor, view, event, onError) {
  const items = event.clipboardData?.items
  if (!items?.length) return false

  for (const item of items) {
    if (!item.type?.startsWith('image/')) continue
    const file = item.getAsFile()
    if (!file) continue
    event.preventDefault()
    insertCompressedImage(editor, file).then((err) => {
      if (err && onError) onError(err)
    })
    return true
  }
  return false
}

/**
 * TipTap drop handler for image files.
 */
export function handleRichTextImageDrop(editor, view, event, onError) {
  const files = event.dataTransfer?.files
  if (!files?.length) return false
  const images = [...files].filter((f) => f.type?.startsWith('image/'))
  if (!images.length) return false
  event.preventDefault()
  ;(async () => {
    for (const file of images) {
      const err = await insertCompressedImage(editor, file)
      if (err && onError) onError(err)
    }
  })()
  return true
}
